import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'docsite-install-dismissed-at';
const DISMISS_DAYS = 14;

/**
 * Non-intrusive PWA install prompt. Listens for `beforeinstallprompt`,
 * shows a subtle bottom-right pill after 8 s, respects a 14-day snooze
 * when the user dismisses. Never blocks content. Hidden on desktop wide
 * viewports by default — mobile/tablet only.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;
    if (window.location.pathname.startsWith('/recovery/')) return; // recovery page has its own install nudge

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && (Date.now() - dismissedAt) < DISMISS_DAYS * 24 * 3600 * 1000) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setVisible(true), 8000);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setVisible(false);
    setDeferred(null);
  };

  if (!visible || !deferred) return null;
  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 right-4 left-4 md:left-auto md:w-80 z-50 rounded-2xl shadow-xl bg-white border border-slate-200 p-3 flex items-center gap-3"
      role="dialog"
      aria-label="Install DocSite app"
    >
      <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
        <Download size={18} className="text-sky-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">Install DocSite</p>
        <p className="text-xs text-slate-500">Works offline. Opens instantly. No app store.</p>
      </div>
      <button
        onClick={install}
        data-testid="pwa-install-btn"
        className="px-3 py-1.5 rounded-full bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700"
      >
        Install
      </button>
      <button
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        className="text-slate-400 hover:text-slate-600"
        aria-label="Dismiss install prompt"
      >
        <X size={16} />
      </button>
    </div>
  );
}
